/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DXP_ACTIVE_CLASS } from '../../../../Constants/Design';
import { ICON_CHEVRON_LEFT } from '../../../../Icons';
import { SdsIcon } from '../../../SdsIcon';
import { NavigationItem } from '../../defs/NavigationItem.defs';
import { SideNavItemProps } from './SideNavItem.props';

/**
 * Main navigation menu item.
 */
export const SideNavItem = (props: SideNavItemProps): JSX.Element => {
  const { level, menuItem } = props;
  const thisMenuItem = { ...menuItem };
  const location = useLocation();
  const navigate = useNavigate();
  const itemIcon = level ? undefined : thisMenuItem.icon;
  const itemId = useUniqueId();

  const currentPath = location.pathname;
  const currentPathIndex = currentPath.indexOf(menuItem.path);
  const [selectedItem, setSelectedItem] = useState(currentPath);
  const [expanded, setExpanded] = useState(currentPathIndex === 0);

  const matchPath = (pathPattern: string, pathname: string): boolean => {
    const pathSegments = pathPattern.split('/');
    const pathnameSegments = pathname.split('/');

    if (pathSegments.length !== pathnameSegments.length) {
      return false;
    }

    return pathSegments.every((segment, i) => segment.startsWith(':') || segment === pathnameSegments[i]);
  };
  const validatePath = (pathPatterns: Array<string>, pathname: string): boolean => {
    return pathPatterns.some((pattern) => matchPath(pattern, pathname));
  };

  const isLinkedScreen = validatePath(thisMenuItem.linkedScreens?.map((screen) => screen.path) || [], currentPath);

  // Items that have internal page paths
  const handlePageNavigation = useCallback(
    (itemPath: string): void => {
      setSelectedItem(itemPath);
      navigate(itemPath);
    },
    [navigate],
  );

  // Child toggle
  const setChildToggle = useCallback((): void => {
    setExpanded(!expanded);
  }, [expanded]);

  useEffect(() => {
    setSelectedItem(currentPath);
  }, [currentPath]);

  const itemLabel = thisMenuItem.label;
  const itemPath = thisMenuItem.path;
  const itemClass = clsx(`sds-sidenav-item`, {
    [DXP_ACTIVE_CLASS]: selectedItem === itemPath || isLinkedScreen,
  });
  const linkClass = clsx(`sds-sidenav-link`, {
    [DXP_ACTIVE_CLASS]: expanded,
  });

  // Handle submenu generation
  const getSubMenuItems = (): Array<JSX.Element> => {
    const childItems = thisMenuItem.children as Array<NavigationItem>;

    return childItems.map((item): JSX.Element => {
      const menuLevel = level ? level + 1 : 2;

      return (
        <React.Fragment key={item.key}>
          <SideNavItem level={menuLevel} menuItem={item} />
        </React.Fragment>
      );
    });
  };

  // Handle different states
  let submenuToggle = null;

  if (thisMenuItem.children) {
    let toggleClass = `sds-sidenav-submenu__toggle sds-sidenav-submenu__toggle--`;
    toggleClass += level ? `sub` : `top`;

    // Override or set an action
    thisMenuItem.action = setChildToggle;

    // Set the JSX
    submenuToggle = (
      <div className={toggleClass}>
        <span className="sr-only">Toggle display of the sub menu</span>
        <SdsIcon svg={ICON_CHEVRON_LEFT} />
      </div>
    );
  }

  return (
    <li className={itemClass}>
      {thisMenuItem.action ? (
        <>
          <button
            aria-controls={`sds-sidenav-submenu--${itemId}`}
            aria-expanded={expanded}
            className={linkClass}
            data-id={`${itemLabel} Menu Item`}
            onClick={(): void => handleItemClick(thisMenuItem)}
          >
            {level ? (
              <>
                {submenuToggle}
                <span className="sds-sidenav-label">{itemLabel}</span>
              </>
            ) : (
              <>
                {itemIcon && <SdsIcon svg={itemIcon} />}
                <span className="sds-sidenav-label">{itemLabel}</span>
                {submenuToggle}
              </>
            )}
          </button>
          {thisMenuItem.children && (
            <ul className="sds-sidenav-submenu" id={`sds-sidenav-submenu--${itemId}`}>
              {getSubMenuItems()}
            </ul>
          )}
        </>
      ) : (
        <button
          aria-current={selectedItem === itemPath && `page`}
          className={linkClass}
          data-id={`${itemLabel} Menu Item`}
          onClick={(): void => handlePageNavigation(itemPath)}
        >
          {itemIcon && <SdsIcon svg={itemIcon} />}
          <span className="sds-sidenav-label">{itemLabel}</span>
        </button>
      )}
    </li>
  );
};

/**
 * Click handler for menu items that have clickable actions.
 * @param {NavigationItem} item The NavigationItem that was clicked.
 */
const handleItemClick = (item: NavigationItem): void => {
  // Item-specific event
  item.action && item.action();
};
